import { useMutation } from '@apollo/client';
import type { InsertDocumentInternalAuditResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertDocumentInternalAuditResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertDocumentInternalAuditResultInput } from './useInsertDocumentInternalAuditResultTRPC';
import { useInsertDocumentInternalAuditResultTRPC } from './useInsertDocumentInternalAuditResultTRPC';

export const useInsertDocumentInternalAuditResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(
    InsertDocumentInternalAuditResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'document_internal_audit_result');
        evictField(cache, 'internal_audit_report');
        evictField(cache, 'document_internal_audit_result_aggregate');
      },
    }
  );

  const trpcMutation = useInsertDocumentInternalAuditResultTRPC();

  const insertDocumentInternalAuditResult = async (
    variables: InsertDocumentInternalAuditResultInput
  ): Promise<InsertDocumentInternalAuditResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertDocumentInternalAuditResult(variables);
    }

    const result = await insertGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to insert document internal audit result');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertDocumentInternalAuditResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertDocumentInternalAuditResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
